import axios from '../../../../shared/axios';
import { AppDispatch } from '../../../../store';

import * as actionTypes from '../constants/actionTypes';

import {
  CertificateDescriptionTemplate,
  Claim,
  DataLookup,
  InsuredPhone,
  NoteType,
  Policy,
  ReminderEmail,
  Task,
  TruckingCompany
} from '../../../../shared/models';

const getNoteTemplatesStart = () => {
  return {
    type: actionTypes.GET_NOTE_TEMPLATES_START,
  };
};

const getNoteTemplatesSuccess = (data: CertificateDescriptionTemplate[]) => {
  return {
    type: actionTypes.GET_NOTE_TEMPLATES_SUCCESS,
    data: data,
  };
};

const getNoteTemplatesFail = (error: any) => {
  return {
    type: actionTypes.GET_NOTE_TEMPLATES_FAIL,
    error: error,
  };
};

export const getNoteTemplates = () => {
  return (dispatch: AppDispatch) => {
    return new Promise<CertificateDescriptionTemplate[]>((resolve, reject) => {
      dispatch(getNoteTemplatesStart());
      const url = `/notes/get-note-templates`;

      axios
        .get(url)
        .then((response) => {
          dispatch(getNoteTemplatesSuccess(response.data));
          resolve(response.data);
        })
        .catch((err) => {
          dispatch(getNoteTemplatesFail(err));
          reject(err);
        });
    });
  };
};

const getPoliciesStart = () => {
  return {
    type: actionTypes.GET_POLICIES_START,
  };
};

const getPoliciesSuccess = (data: Policy[]) => {
  return {
    type: actionTypes.GET_POLICIES_SUCCESS,
    data: data,
  };
};

const getPoliciesFail = (error: any) => {
  return {
    type: actionTypes.GET_POLICIES_FAIL,
    error: error,
  };
};

export const getPolicies = (insuredId?: string, searchString?: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(getPoliciesStart());

    const url = `/notes/get-policies`;

    axios
      .get(url, { params: { insuredId, searchString } })
      .then((response) => dispatch(getPoliciesSuccess(response.data)))
      .catch((err) => dispatch(getPoliciesFail(err)));
  };
};

const getTasksStart = () => {
  return {
    type: actionTypes.GET_TASKS_START,
  };
};

const getTasksSuccess = (data: Task[]) => {
  return {
    type: actionTypes.GET_TASKS_SUCCESS,
    data: data,
  };
};

const getTasksFail = (error: any) => {
  return {
    type: actionTypes.GET_TASKS_FAIL,
    error: error,
  };
};

export const getTasks = (insuredId?: string | null | undefined, searchString?: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(getTasksStart());
    const insuredIdQueryParam = !!insuredId ? `insuredId=${insuredId}` : '';
    const searchPhraseQuery = !!searchString ? `&searchString=${searchString}` : '';
    const url = `/notes/get-tasks?${insuredIdQueryParam}${searchPhraseQuery}`;

    axios
      .get(url)
      .then((response) => dispatch(getTasksSuccess(response.data)))
      .catch((err) => dispatch(getTasksFail(err)));
  };
};

const getClaimsStart = () => {
  return {
    type: actionTypes.GET_CLAIMS_START,
  };
};

const getClaimsSuccess = (data: Claim[]) => {
  return {
    type: actionTypes.GET_CLAIMS_SUCCESS,
    data: data,
  };
};

const getClaimsFail = (error: any) => {
  return {
    type: actionTypes.GET_CLAIMS_FAIL,
    error: error,
  };
};

export const getClaims = (insuredId?: string, searchString?: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(getClaimsStart());
    const url = `/notes/get-claims`;

    axios
      .get(url, { params: { insuredId, searchString } })
      .then((response) => dispatch(getClaimsSuccess(response.data)))
      .catch((err) => dispatch(getClaimsFail(err)));
  };
};

const getNoteTypesStart = () => {
  return {
    type: actionTypes.GET_NOTE_TYPES_START,
  };
};

const getNoteTypesSuccess = (data: NoteType[]) => {
  return {
    type: actionTypes.GET_NOTE_TYPES_SUCCESS,
    data: data,
  };
};

const getNoteTypesFail = (error: any) => {
  return {
    type: actionTypes.GET_NOTE_TYPES_FAIL,
    error: error,
  };
};

export const getNoteTypes = (searchString?: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(getNoteTypesStart());

    const url = `/notes/get-note-types`;

    axios
      .get(url, { params: { searchString } })
      .then((response) => {
        dispatch(getNoteTypesSuccess(response.data));
      })
      .catch((err) => dispatch(getNoteTypesFail(err)));
  };
};

const createNoteTypeStart = () => {
  return {
    type: actionTypes.CREATE_NOTE_TYPE_START,
  };
};

const createNoteTypeSuccess = (data: any) => {
  return {
    type: actionTypes.CREATE_NOTE_TYPE_SUCCESS,
  };
};

const createNoteTypeFail = (error: any) => {
  return {
    type: actionTypes.CREATE_NOTE_TYPE_FAIL,
    error: error,
  };
};

export const createNoteType = (formData: any) => {
  return (dispatch: AppDispatch) => {
    dispatch(createNoteTypeStart());

    const url = `/notes/create-note-type`;

    axios
      .post(url, formData)
      .then((response) => {
        dispatch(createNoteTypeSuccess(response.data));
        dispatch(getNoteTypes());
      })
      .catch((err) => {
        dispatch(createNoteTypeFail(err));
      });
  };
};

const getContactPhonesStart = () => {
  return {
    type: actionTypes.GET_CONTACT_PHONES_START,
  };
};

const getContactPhonesSuccess = (data: InsuredPhone[]) => {
  return {
    type: actionTypes.GET_CONTACT_PHONES_SUCCESS,
    data: data,
  };
};

const getContactPhonesFail = (error: any) => {
  return {
    type: actionTypes.GET_CONTACT_PHONES_FAIL,
    error: error,
  };
};

export const getContactPhones = (params: any) => {
  return (dispatch: AppDispatch) => {
    dispatch(getContactPhonesStart());
    const url = `/notes/get-contact-phones`;

    axios
      .get(url, {
        params: params,
      })
      .then((response) => dispatch(getContactPhonesSuccess(response.data)))
      .catch((err) => dispatch(getContactPhonesFail(err)));
  };
};

const getReminderEmailsStart = () => {
  return {
    type: actionTypes.GET_REMINDER_EMAILS_START,
  };
};

const getReminderEmailsSuccess = (data: ReminderEmail[]) => {
  return {
    type: actionTypes.GET_REMINDER_EMAILS_SUCCESS,
    data: data,
  };
};

const getReminderEmailsFail = (error: any) => {
  return {
    type: actionTypes.GET_REMINDER_EMAILS_FAIL,
    error: error,
  };
};

export const getReminderEmails = () => {
  return (dispatch: AppDispatch) => {
    dispatch(getReminderEmailsStart());

    const url = `/notes/get-reminder-emails`;

    axios
      .get(url)
      .then((response) => dispatch(getReminderEmailsSuccess(response.data)))
      .catch((err) => dispatch(getReminderEmailsFail(err)));
  };
};

const getInsuredsProspectsStart = () => {
  return {
    type: actionTypes.GET_INSUREDS_PROSPECTS_START,
  };
};

const getInsuredsProspectsSuccess = (data: TruckingCompany[]) => {
  return {
    type: actionTypes.GET_INSUREDS_PROSPECTS_SUCCESS,
    data: data,
  };
};

const getInsuredsProspectsFail = (error: any) => {
  return {
    type: actionTypes.GET_INSUREDS_PROSPECTS_FAIL,
    error: error,
  };
};

export const getInsuredsProspects = (props: any) => {
  return (dispatch: AppDispatch) => {
    dispatch(getInsuredsProspectsStart());

    const url = `/shared/get-insureds-prospects`;

    axios
      .get(url, { params: props })
      .then((response) => {
        dispatch(getInsuredsProspectsSuccess(response.data));
      })
      .catch((err) => dispatch(getInsuredsProspectsFail(err)));
  };
};

const getNoteDispositionsStart = () => {
  return {
    type: actionTypes.GET_NOTE_DISPOSITIONS_START,
  };
};

const getNoteDispositionsSuccess = (data: DataLookup) => {
  return {
    type: actionTypes.GET_NOTE_DISPOSITIONS_SUCCESS,
    data: data,
  };
};

const getNoteDispositionsFail = (error: any) => {
  return {
    type: actionTypes.GET_NOTE_DISPOSITIONS_FAIL,
    error: error,
  };
};

export const getNoteDispositions = (props: any) => {
  return (dispatch: AppDispatch) => {
    dispatch(getNoteDispositionsStart());

    const url = `/shared/get-note-dispositions`;

    axios
      .get(url, { params: props })
      .then((response) => {
        dispatch(getNoteDispositionsSuccess(response.data));
      })
      .catch((err) => dispatch(getNoteDispositionsFail(err)));
  };
};
